/**
 *
 * generateThenUploadTemplateZip - this part was moved from old SCM and
 * must be upgraded when new Savant upload service will implemented
 *
 */

/*
 * SCM will support dealers uploading rpmConfigs to domains (as templates) and jobs.
 * When a dealer drops an rpmConfig package into the upload area, SCM
 * will make a copy of the package, zip the duplicate package, and upload
 * the new package to SCS.
 * SCS requires:
 * .that a config Id be unique to a job or domain
 * .metadata in the upload request including the config Id and the host UID
 *
 * While SCM is traversing the dropped package, it will extract this data
 * and potentially edit the cloned documentInfo.plist with a new config id
 * to allow dealers to upload their config without config Id cloud errors.
 */

/**
 * @todo reuse existing logic, instead of duplicating
 * needs to update logic for doing current suggestion
 */

import { configUploadParseBuffer } from './bplist-parser'
import { authorizedEdgeFetch } from 'app/axios/edge'
import _isEqual from 'lodash/isEqual'
import _map from 'lodash/map'
import _slice from 'lodash/slice'
import _get from 'lodash/get'
import { v4 as uuidv4 } from 'uuid'
import { templatesPageMessages } from 'features/templates/messages'
import { authorizedConfigUploadFetch } from 'app/axios/upload'
import { axiosProgressUploading } from './httpProgressRequest'

const JSZip = require('jszip')
const parseXMLString = require('xml2js').parseString
const plist = require('plist')
const BufferPolyfill = require('buffer')
const { Buffer } = BufferPolyfill
window.Buffer = Buffer

function getConfigDomainId(configId) {
  return authorizedEdgeFetch.get(`/config/${configId}/domain`)
}

async function postConfigToDomain(userId, params) {
  const res = await authorizedConfigUploadFetch.post(
    `/config/domain/${params.domain_Id}`,
    {
      integrator_id: userId,
      comment: null,
      config_id: params.config_id,
      name: params.name,
      host_uid: params.host_uid,
      extension: 'zip',
    }
  )

  return res?.data?.payload?.configUploadUrl
}

/*
 * Receives a dropped file entry of an rpmConfig in order to:
 * .generate a new zip folder of that config to upload
 * .prepare upload request paramaters
 * SCM then calls the passed callback entry with those parameters
 */
export function homeConfigGenerateThenUploadTemplateZip(
  droppedFile,
  domainId,
  userId,
  formatMessage,
  setUploadingPercents
) {
  // Declaring variables required by all functions inside generateThenUploadConfig
  const fileName = droppedFile.name.trim()
  const params = {
    name: fileName,
    config_id: null,
    domain_Id: domainId,
    host_uid: null,
  }

  /*
   * Rejects the dropped rpmConfig if it does not have the correct extension
   */
  const validateFileExtension = () => {
    const invalidFileExtension = !fileName.endsWith('.rpmConfig')
    if (invalidFileExtension) {
      return Promise.reject(
        formatMessage(templatesPageMessages.uploadTemplateResolutionError, {
          fileName,
        })
      )
    }
    return Promise.resolve()
  }

  /*
   * Resolves with a new JSZip object
   */
  const initializeZip = () => {
    const zip = new JSZip()
    const configZip = zip.folder(droppedFile.name)
    return Promise.resolve(configZip)
  }

  /*
   * Recursively traverses the dropped file directory and
   * adds the files to the new config folder SCM is generating
   */
  const addConfigFilesToZip = (configZip, currentlyTraversing) => {
    // The first call to addConfigFilesToZip does not pass a currentlyTraversing object
    // If SCM is not currently traversing an entry, start traversing the dropped file
    const entry = currentlyTraversing || droppedFile
    const reader = entry.createReader()

    /* Returns a promise for each entry in the dropped file directory */
    const handleEntry = (entryItem) => {
      const handleReject = (err) => Promise.reject(err)
      if (entryItem.isFile) {
        // If an entry item is a file, add it to the zip folder
        return handleFileEntry(entryItem).catch(handleReject)
      }
      // Else entryItem is a directory
      // SCM will make a recursive call to resolve the sub-directories and files
      return addConfigFilesToZip(configZip, entryItem).catch(handleReject)
    }

    /* Resolves after adding file entry to zip folder */
    const handleFileEntry = (fileEntry) =>
      new Promise((resolveFileEntry, rejectFileEntry) => {
        const addToZipAndResolve = (file) => {
          addFileToZip(file)
          return resolveFileEntry()
        }

        const handleFileExtracted = (file) => {
          if (file.name === 'documentInfo.plist') {
            // Discern the configId in the documentInfo file
            // SCM may generate an updated documentInfo file for the new config upload
            handleDocumentInfoConfigIdInjection(file)
              .then(addToZipAndResolve)
              .catch(rejectFileEntry)
          } else if (file.name === 'zoneConfig.xml') {
            // Discern the host uid from the zoneConfig file
            // SCM makes no changes to this file
            handleZoneConfigHostUidExtraction(file)
              .then(addToZipAndResolve)
              .catch(rejectFileEntry)
          } else {
            addToZipAndResolve(file)
          }
        }

        // Extract file object from file entry object
        fileEntry.file(handleFileExtracted)
      })

    /* SCM must discern and include the config Id in the upload parameters as metadata */
    const handleDocumentInfoConfigIdInjection = (documentInfoPlist) =>
      new Promise((resolveDocumentInfo, rejectDocumentInfo) => {
        const fileReader = new FileReader()
        let configId
        let plistAsJson

        // Document info file was successfully read
        const onDocumentInfoLoaded = () => {
          convertPlistToJson(fileReader.result)
            .then(extractIdFromDroppedPlistConfig)
            .then(getConfigDomainId)
            .then(discernConfigIdAssociation) // if no new configId is needed, this function escapes the promise chain
            .then(injectNewConfigId)
            .catch(onDocumentInfoError)
        }

        // Error reading document info file
        const onDocumentInfoError = (err) => {
          if (err.message === 'Config does not exist') {
            // TODO: The API changes are not in every environment! This will not be
            //  necessary once getConfigDomainId does not return errors.
            //  Cloud API for getConfigDomainId returns an Error instead of null when
            //  SCM requests a config's associated domain id and it does not yet have
            //  one. In this instance, the dropped plist is valid and SCM resolves it.
            resolveDocumentInfo(documentInfoPlist)
          } else {
            rejectDocumentInfo(err)
          }
        }

        // Handles convertig xml plists and bplists to JSON objects
        const convertPlistToJson = (fileReaderResult) =>
          new Promise((resolveJsonPlist) => {
            // Determine if documentInfo.plist is XML plist or binary plist (bplist)
            const byteArray = Array.from(new Int8Array(fileReaderResult))
            const documentInfoFileHeader = _slice(byteArray, 0, 6) // bplist headers are the first 6 characters of a file
            const requiredBplistHeader = [98, 112, 108, 105, 115, 116] // ['b', 'p', 'l', 'i', 's', 't'] This begins every binary plist file
            const plistIsBplist = _isEqual(
              documentInfoFileHeader,
              requiredBplistHeader
            ) // true if the first 6 characters of documentInfo are 'bplist'

            if (plistIsBplist) {
              convertBplistToJson(fileReaderResult).then(resolveJsonPlist)
            } else {
              // else this is an XML plist
              convertXmlPlistToJson(byteArray).then(resolveJsonPlist)
            }
          })

        const convertBplistToJson = (byteArray) =>
          Promise.resolve({
            payload: configUploadParseBuffer(Buffer.from(byteArray)),
          })
        const convertData = (byte) => String.fromCharCode(byte)
        const convertXmlPlistToJson = (byteArray) => {
          const reconstructedXMLFileFromBytes = _map(
            byteArray,
            convertData
          ).join('')
          const plistParsedFromXml = plist.parse(reconstructedXMLFileFromBytes)
          // extractIdFromDroppedPlistConfig expects an API response with a payload
          // plistToJson calls an API endpoint, but xmlPlistToJson can be executed
          // client side. SCM will simulate an API response in order to reuse code
          return Promise.resolve({
            payload: [plistParsedFromXml],
          })
        }

        // After converting plist to json object, extract the config Id
        const extractIdFromDroppedPlistConfig = (plistAsJsonAPIResponse) => {
          plistAsJson = _get(plistAsJsonAPIResponse, 'payload[0]')
          configId = _get(plistAsJson, 'RPMDocInfoGUIDKey')
          configId = configId.replace('Document_', '')
          params.config_id = configId
          return Promise.resolve(configId)
          // The API to retrieve the config id's associated domain id
          // responds with an error if the config id does not exist
          // in any domain or job already. If SCM receives an error,
          // catch it and call discernConfigIdAssociation anyways as
          // SCM's functionality does not consider this an error
        }

        // When SCS responds with config Id's associated domain id,
        // determine if it belongs to the current job/domain.
        // Two configs with the same configId cannot exist in different jobs.
        const discernConfigIdAssociation = (associatedDomainIdResp) => {
          const associatedDomainId = _get(
            associatedDomainIdResp,
            'data.payload.domainId',
            null
          )

          const configBelongsToCurrentDomain = associatedDomainId === domainId
          // If configId's domain id is the same domain SCM is uploading to,
          // or if the config id has no associated domain id, SCS will
          // replace the existing config with the dropped config.
          // SCM will use the current config Id and resolve the dropped documentInfo file
          if (!associatedDomainId || configBelongsToCurrentDomain) {
            params.config_id = configId
            // this will escape the promise chains required for updating documentInfo file
            return resolveDocumentInfo(documentInfoPlist)
          }

          // Else, SCM will generate a new config Id and resolve a new documentInfo file
          const newConfigId = uuidv4()
          params.config_id = newConfigId
          return Promise.resolve(newConfigId)
        }

        const injectNewConfigId = (newConfigId) => {
          plistAsJson.RPMDocInfoGUIDKey = 'Document_'.concat(newConfigId)
          const newPlist = plist.build(plistAsJson)
          resolveNewDocumentInfoFile(newPlist)
          // If SCM needs to support exporting bplists, make the API call here
          // plistFromJson(plistAsJson).then(resolveNewDocumentInfoFile)
        }

        // After converting json document info into a plist byte array
        // convert it into a regular file object and resolve the new info file
        const resolveNewDocumentInfoFile = (plistData) => {
          const plistFile = new File([plistData], 'documentInfo.plist')
          resolveDocumentInfo(plistFile)
        }

        // Call the reader for document info
        fileReader.addEventListener('loadend', onDocumentInfoLoaded)
        fileReader.addEventListener('error', onDocumentInfoError)
        fileReader.readAsArrayBuffer(documentInfoPlist)
      })

    /* SCS requires the host UID as metadata when SCM uploads a config */
    const handleZoneConfigHostUidExtraction = (file) =>
      new Promise((resolveZoneConfig, rejectZoneConfig) => {
        const fileReader = new FileReader()

        // Extract the host UID from the parsed zoneConfig xml file
        const xmlParsingHandler = (err, json) => {
          if (err) {
            rejectZoneConfig(
              new Error(
                formatMessage(
                  templatesPageMessages.uploadTemplateParsingError,
                  {
                    message: err.message || `${err}`,
                  }
                )
              )
            )
          }

          const hostUID = _get(
            json,
            'zone_config.zone_master[0].$.chassis_id',
            null
          )

          // Adds extracted host UID to config upload parameters
          params.host_uid = hostUID
          resolveZoneConfig(file)
        }

        // XML file was successfully read
        const onLoad = () => {
          parseXMLString(fileReader.result, xmlParsingHandler)
        }

        // Error in XML file file reader
        const onError = (err) => {
          rejectZoneConfig(err)
        }

        fileReader.addEventListener('loadend', onLoad)
        fileReader.addEventListener('error', onError)
        fileReader.readAsText(file)
      })

    /* Synchronously adds provided file to new config zip directory */
    const addFileToZip = (file) => {
      configZip.file(entry.fullPath.concat('/', file.name), file)
    }

    /* Resolves once all sub-directories and files have resolved */
    return new Promise((resolveDirectory, rejectDirectory) => {
      // An array of all sub-directories and files to be resolved with Promise.all
      const iterationAttempts = []

      const handleResolveDirectory = () => resolveDirectory(configZip)

      // Entries successfully extracted from directory entry
      const readEntriesSuccess = (entries) => {
        if (!entries.length) {
          // Done iterating this particular directory
          Promise.all(iterationAttempts).then(handleResolveDirectory)
        } else {
          const handleReject = (err) => {
            rejectDirectory(err)
          }
          // Add a list of promises for each directory entry.  If the entry is itself
          // a directory, then that promise won't resolve until it is fully traversed.
          iterationAttempts.push(
            Promise.all(entries.map(handleEntry)).catch(handleReject)
          )
          // Try calling readEntries() again for the same dir, according to spec
          readDirectory()
        }
      }

      // Error extracting entries from directory entry
      const readEntriesError = (err) => rejectDirectory(err)

      // Attempt to read entries from directory entry
      const readDirectory = () => {
        reader.readEntries(readEntriesSuccess, readEntriesError)
      }

      // Begin traversing the directory
      readDirectory()
    })
  }

  /*
   * Resolves after generating zip from initialized zip directory
   */
  const generateZip = (configZip) => {
    const onGenerateZipError = () =>
      Promise.reject(
        new Error(formatMessage(templatesPageMessages.uploadTemplateZipError))
      )
    return configZip
      .generateAsync({
        type: 'blob',
      })
      .catch(onGenerateZipError)
  }

  /*
   * After generating the zip file, call the provided callback function
   */
  const onZipSuccess = async (zippedData) => {
    const uploadUrl = await postConfigToDomain(userId, params)
    if (uploadUrl) {
      return await axiosProgressUploading(
        uploadUrl,
        zippedData,
        setUploadingPercents
      )
    }
  }

  /*
   * Begin building zip folder for upload from dropped rpmConfig file
   */
  return validateFileExtension(fileName)
    .then(initializeZip)
    .then(addConfigFilesToZip)
    .then(generateZip)
    .then(onZipSuccess)
    .then(() => params.host_uid)
    .catch((err) => {
      if (err.response?.data?.error?.code === 188) {
        throw new Error(
          formatMessage(
            templatesPageMessages.uploadTemplateAlreadyAttachedError
          )
        )
      }
      throw new Error(
        formatMessage(templatesPageMessages.uploadTemplateFileError)
      )
    })
}
